import CryptoJS from "crypto-js";

// ---------------------------------------------------------------------------
// Encryption setup (mirrors Cookies.js)
// ---------------------------------------------------------------------------
const ENCRYPTED_PREFIX = "ENC:";

const secretKey = process.env.VUE_APP_ENCRYPTION_KEY;
const isEncryptionEnabled =
  typeof secretKey === "string" && secretKey.length > 0;

if (!isEncryptionEnabled && typeof window !== "undefined") {
  console.warn(
    "[localStorageControl] Encryption key (VUE_APP_ENCRYPTION_KEY) is missing. " +
      "localStorage values will be stored without encryption."
  );
}

/**
 * Try to parse a JSON string; return the raw value on failure.
 * @param {string} value
 * @returns {any}
 */
const tryParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * AES-encrypt a value and prefix it with ENCRYPTED_PREFIX.
 * Non-string values are JSON-stringified first.
 * @param {any} value
 * @returns {string}
 */
const encryptValue = (value) => {
  if (value === null || value === undefined) return value;
  if (!isEncryptionEnabled)
    return typeof value === "string" ? value : JSON.stringify(value);

  const stringValue = typeof value === "string" ? value : JSON.stringify(value);

  return `${ENCRYPTED_PREFIX}${CryptoJS.AES.encrypt(
    stringValue,
    secretKey
  ).toString()}`;
};

/**
 * Decrypt a value that was encrypted by encryptValue.
 * Falls back gracefully if decryption fails or the value was never encrypted.
 * @param {any} value
 * @returns {any}
 */
const decryptValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "string") return value;

  if (!isEncryptionEnabled) return tryParseJson(value);

  if (!value.startsWith(ENCRYPTED_PREFIX)) {
    // Legacy plain-text value — return as-is (parsed if JSON)
    return tryParseJson(value);
  }

  try {
    const encrypted = value.slice(ENCRYPTED_PREFIX.length);
    const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return tryParseJson(decrypted);
  } catch {
    // Decryption failed — return raw value to avoid crashes
    return tryParseJson(value);
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve and decrypt a value from localStorage.
 * @param {string} key
 * @returns {any|null}
 */
const getItem = (key) => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  return decryptValue(raw);
};

/**
 * Encrypt and store a value in localStorage.
 * @param {string} key
 * @param {any} value
 */
const setItem = (key, value) => {
  if (typeof window === "undefined") return;
  const encoded = encryptValue(value);
  localStorage.setItem(key, encoded);
};

/**
 * Remove a key from localStorage.
 * @param {string} key
 */
const removeItem = (key) => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(key);
};

export { getItem, setItem, removeItem };
